/**
 * Global Error Handlers for AppSignal
 *
 * Sets up global error handlers to catch errors that React ErrorBoundary doesn't catch:
 * - Unhandled JavaScript errors (window.onerror)
 * - Unhandled promise rejections
 * - Resource loading errors (scripts, images, etc.)
 *
 * These handlers integrate with AppSignal to report errors that occur outside
 * of React's component lifecycle.
 *
 * Breadcrumbs (Redux action sequence) are automatically included by AppSignal.
 * This module adds the current Redux state as params for detailed inspection.
 */

import { getReduxState } from "state/middleware/appsignal";

/**
 * Helper to get Redux state and tags for error reporting
 *
 * Breadcrumbs showing action sequence are automatically included by AppSignal.
 * This function adds:
 * - Tags: Small metadata for filtering/searching
 * - Params: Full Redux state for detailed inspection
 *
 * @returns {Object} Object with tags and params
 */
const getReduxContextForError = () => {
  try {
    const state = getReduxState();

    if (!state) {
      return { tags: {}, params: {} };
    }

    const currentUser = state.currentUser;
    const router = state.router;

    // TAGS: Small metadata for filtering/searching in AppSignal
    const tags = {
      redux_user_id: String(currentUser?.id || "not_logged_in"),
      redux_user_name: currentUser?.name || "not_logged_in",
      redux_user_email: currentUser?.email || "not_logged_in"
    };

    // Add useful state slices to tags for quick reference
    if (router && router.location) {
      tags.redux_current_path = router.location.pathname;
      tags.redux_current_search = router.location.search || "";
    }

    if (currentUser) {
      tags.redux_user_token_origin = currentUser.tokenOrigin || "";
    }

    // Note: Redux state is available via getReduxState() if needed for debugging,
    // but we don't send it by default to avoid cluttering the AppSignal dashboard.
    // Breadcrumbs provide the action sequence which is usually more useful.

    return { tags, params: {} };
  } catch (error) {
    console.error("Failed to get Redux context for error", error);
    return { tags: {}, params: {} };
  }
};

/**
 * Initialize global error handlers for AppSignal
 * @param {Object} appsignal - The AppSignal instance
 */
export const initializeGlobalErrorHandlers = appsignal => {
  // Skip if appsignal is not available or in test environment
  if (!appsignal || process.env.NODE_ENV === "test") {
    return;
  }

  // Catch unhandled JavaScript errors
  window.addEventListener("error", event => {
    // Skip resource loading errors - only handle JavaScript errors
    if (event.target !== window) {
      return;
    }

    if (event.error) {
      const { tags } = getReduxContextForError();

      appsignal.sendError(event.error, span => {
        span.setAction("window.onerror");
        span.setTags({
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          ...tags
        });
      });
    }
  });

  // Catch unhandled promise rejections (including network errors)
  window.addEventListener("unhandledrejection", event => {
    // Create an Error object if the rejection reason isn't already one
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    const { tags } = getReduxContextForError();

    appsignal.sendError(error, span => {
      span.setAction("unhandledrejection");
      span.setTags({
        type: "promise_rejection",
        reason: String(event.reason),
        ...tags
      });
    });
  });

  // Resource loading errors are NOT reported to AppSignal
  //
  // We previously reported all resource loading errors (images, scripts, stylesheets, etc.)
  // but these were creating too much noise in AppSignal because:
  //
  // 1. Many are external third-party resources (Google Ads, analytics, CDN failures)
  //    that we have no control over
  // 2. These errors are not actionable - we can't fix third-party infrastructure
  // 3. Critical resource failures (our own JS/CSS bundles) will fail loudly in other ways
  //    (application won't load, React errors, etc.)
  //
  // If you need to debug resource loading issues, use browser DevTools Network tab.
  //
  // Previously removed code: Resource loading error event listener (lines 143-193)

  console.info("AppSignal: Global error handlers initialized");
};

/**
 * Create a test error helper function for browser console testing
 * This function is exposed on window for easy testing
 * Usage: window.testAppSignalError("My test error message")
 *
 * @param {Object} appsignal - The AppSignal instance
 */
export const createTestErrorHelper = appsignal => {
  window.testAppSignalError = (message = "Test error for AppSignal") => {
    try {
      const error = new Error(message);
      const { tags } = getReduxContextForError();

      console.group("📤 Sending test error to AppSignal");
      console.log("Message:", message);
      console.log("Tags (metadata):", tags);
      console.log(
        "Note: Breadcrumbs showing Redux action sequence (with action data) will be automatically included!"
      );
      console.groupEnd();

      appsignal.sendError(error, span => {
        span.setAction("manual_test_error");
        span.setTags({
          test_error: true,
          triggered_from: "browser_console",
          ...tags
        });
      });

      console.log(
        "✅ Test error sent to AppSignal! Check your dashboard in a few minutes.\n" +
          "   - Breadcrumbs tab: See Redux action sequence with action data\n" +
          "   - Tags: User ID, path, and other metadata"
      );
      return "Error sent successfully";
    } catch (err) {
      console.error("❌ Failed to send test error:", err);
      return "Error sending failed";
    }
  };

  // Helper to view the current Redux state
  window.viewReduxState = () => {
    try {
      const state = getReduxState();

      if (!state) {
        console.warn(
          "Redux state not available yet. Store may not be initialized."
        );
        return null;
      }

      const currentUser = state?.currentUser;

      console.group("📊 Current Redux State");
      console.log("User ID:", currentUser?.id || "not logged in");
      console.log("Current Path:", state.router?.location?.pathname || "");
      console.log("Current Team:", state.ui?.currentTeam?.id || "");
      console.groupEnd();

      console.group("🔍 Full Redux State");
      console.log(state);
      console.groupEnd();

      return state;
    } catch (err) {
      console.error("Failed to view Redux state:", err);
      return null;
    }
  };

  console.info(
    "AppSignal: Test helpers available\n" +
      "  - window.testAppSignalError('message') - Send test error with breadcrumbs\n" +
      "  - window.viewReduxState() - View current Redux state"
  );
};
